import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Loader2 } from 'lucide-react';
import type {
  AnalogPricePoint,
  StockAnalogLensData,
  StockAnalogMatch,
  StockAnalogSnapshot,
} from '@/mocks/stockAnalogMock';
import { stockService } from '@/services/api';
import { AnalogForwardMiniChart } from './AnalogForwardMiniChart';
import { AnalogLensOutlookVisual } from './AnalogLensOutlookVisual';
import { formatDateShort, formatPct, pctChipClass } from './AnalogLensAccordion';

type Props = {
  symbol: string;
  referenceDate?: string;
  preloadedSnapshot?: StockAnalogSnapshot | null;
  inline?: boolean;
};

type MechLens = StockAnalogLensData & {
  reference?: { features?: Record<string, number | null> };
  available?: boolean;
  reason?: string;
};

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function buildMedianPricePath(matches: StockAnalogMatch[]): AnalogPricePoint[] {
  const byT = new Map<number, number[]>();
  for (const match of matches) {
    for (const pt of match.pricePath ?? []) {
      if (!Number.isFinite(pt.t) || !Number.isFinite(pt.price)) continue;
      const list = byT.get(pt.t) ?? [];
      list.push(pt.price);
      byT.set(pt.t, list);
    }
  }
  return [...byT.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([t, prices]) => ({ t, price: median(prices) }));
}

function featureChips(
  features: Record<string, number | null> | undefined,
): { label: string; value: string }[] {
  if (!features) return [];
  const chips: { label: string; value: string }[] = [];
  const wr = features.window_return_pct;
  const rsi = features.rsi_14;
  const vol = features.realized_vol_pct;
  const sma50 = features.dist_sma50_pct;
  const dd = features.drawdown_from_high_pct;
  const rvol = features.rel_volume;
  if (typeof wr === 'number') chips.push({ label: '20d', value: formatPct(wr) });
  if (typeof rsi === 'number') chips.push({ label: 'RSI', value: rsi.toFixed(0) });
  if (typeof vol === 'number') chips.push({ label: 'Vol', value: `${vol.toFixed(1)}%` });
  if (typeof sma50 === 'number') chips.push({ label: 'vs SMA50', value: formatPct(sma50) });
  if (typeof dd === 'number') chips.push({ label: 'Drawdown', value: formatPct(dd) });
  if (typeof rvol === 'number') chips.push({ label: 'RVol', value: `${rvol.toFixed(2)}×` });
  return chips;
}

function setupLabel(features: Record<string, number | null> | undefined): string {
  if (!features) return 'Current price fingerprint';
  const wr = features.window_return_pct;
  const rsi = features.rsi_14;
  if (typeof wr === 'number' && wr < -5) return 'Washout / oversold setup';
  if (typeof wr === 'number' && wr > 5) return 'Momentum continuation setup';
  if (typeof rsi === 'number' && rsi >= 70) return 'Extended / elevated RSI';
  if (typeof rsi === 'number' && rsi <= 30) return 'Oversold RSI pocket';
  return 'Range / mid-structure setup';
}

function InlineMessage({
  inline,
  children,
  tone = 'neutral',
}: {
  inline?: boolean;
  children: ReactNode;
  tone?: 'neutral' | 'error' | 'warn';
}) {
  const tones = {
    neutral: 'bg-gray-50 text-gray-500',
    error: 'bg-rose-50 text-rose-800',
    warn: 'bg-amber-50 text-amber-900',
  };
  return (
    <motion.div
      className={`${inline ? 'border-t border-gray-100 px-4 py-4 text-xs' : 'mt-4 rounded-2xl border border-gray-200 px-4 py-6 text-sm'} ${tones[tone]}`}
    >
      {children}
    </motion.div>
  );
}

export function ReliablePickSelfAnalogDetail({
  symbol,
  referenceDate,
  preloadedSnapshot,
  inline = false,
}: Props) {
  const [snapshot, setSnapshot] = useState<StockAnalogSnapshot | null>(
    preloadedSnapshot ?? null,
  );
  const [loading, setLoading] = useState(!preloadedSnapshot);
  const [error, setError] = useState<string | null>(null);
  const [selectedMatchDate, setSelectedMatchDate] = useState<string | null>(null);

  useEffect(() => {
    if (preloadedSnapshot) {
      setSnapshot(preloadedSnapshot);
      setLoading(false);
      setError(null);
      setSelectedMatchDate(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setSnapshot(null);
    setSelectedMatchDate(null);

    (async () => {
      const data = await stockService.getMechanicalAnalogs(symbol, {
        date: referenceDate,
        topK: 20,
      });
      if (cancelled) return;
      if (!data) {
        setError('Could not load self-analogs for this name.');
        setLoading(false);
        return;
      }
      setSnapshot(data);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [symbol, referenceDate, preloadedSnapshot]);

  const lens = useMemo(() => {
    const raw = snapshot?.lenses.find((l) => l.id === 'mechanical') as MechLens | undefined;
    return raw ?? null;
  }, [snapshot]);

  const matches = lens?.matches ?? [];
  const medianPath = useMemo(() => buildMedianPricePath(matches), [matches]);
  const selectedMatch = selectedMatchDate
    ? (matches.find((m) => m.date === selectedMatchDate) ?? null)
    : null;
  const features = lens?.reference?.features;
  const chips = featureChips(features);

  const t5Vals = matches
    .map((m) => m.forwardReturns?.t5)
    .filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
  const selfHit5 =
    t5Vals.length > 0
      ? Math.round((100 * t5Vals.filter((v) => v > 0).length) / t5Vals.length)
      : null;
  const selfMed5 = t5Vals.length ? median(t5Vals) : null;

  if (loading) {
    return (
      <InlineMessage inline={inline}>
        <span className="inline-flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading self-analogs for {symbol}…
        </span>
      </InlineMessage>
    );
  }

  if (error || !snapshot || !lens) {
    return (
      <InlineMessage inline={inline} tone="error">
        {error ?? 'No mechanical analogs available.'}
      </InlineMessage>
    );
  }

  if (!matches.length) {
    return (
      <InlineMessage inline={inline} tone="warn">
        {lens.reason || `No self-analogs found for ${symbol} on this date.`}
      </InlineMessage>
    );
  }

  const shellClass = inline
    ? 'border-t border-emerald-100/80 bg-gradient-to-b from-emerald-50/40 to-white px-4 py-4 sm:px-5'
    : 'mt-4 space-y-4 rounded-2xl border border-gray-200 bg-white p-4';

  return (
    <div className={shellClass}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Price shape today
            </p>
            <p className="mt-0.5 text-sm font-semibold text-gray-900">
              {setupLabel(features)}
              <span className="ml-2 font-normal text-gray-500">
                ${snapshot.close.toFixed(2)}
                <span className={`ml-1.5 font-medium ${pctChipClass(snapshot.dayReturnPct)}`}>
                  {formatPct(snapshot.dayReturnPct)}
                </span>
              </span>
            </p>
          </div>
          {selfHit5 != null && selfMed5 != null ? (
            <div className="flex flex-wrap gap-2">
              <div className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-center">
                <p className="text-[9px] uppercase tracking-wide text-gray-400">Self 5d hit</p>
                <p className="text-sm font-semibold text-gray-900">{selfHit5}%</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-center">
                <p className="text-[9px] uppercase tracking-wide text-gray-400">Self med 5d</p>
                <p className={`text-sm font-semibold ${pctChipClass(selfMed5)}`}>
                  {formatPct(selfMed5)}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-center">
                <p className="text-[9px] uppercase tracking-wide text-gray-400">Analogs</p>
                <p className="text-sm font-semibold text-gray-900">{matches.length}</p>
              </div>
            </div>
          ) : null}
        </div>

        {chips.length ? (
          <div className="flex flex-wrap gap-1.5">
            {chips.map((c) => (
              <span
                key={c.label}
                className="rounded-md bg-white/80 px-2 py-0.5 text-[11px] font-medium text-gray-600 ring-1 ring-gray-200/80"
              >
                {c.label} {c.value}
              </span>
            ))}
          </div>
        ) : null}

        {medianPath.length > 2 ? (
          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Median path · {matches.length} self-analogs
            </p>
            <AnalogForwardMiniChart
              path={medianPath}
              anchorDate={snapshot.referenceDate}
              anchorClose={snapshot.close}
            />
          </div>
        ) : null}

        <AnalogLensOutlookVisual
          lens={lens}
          referenceDate={snapshot.referenceDate}
          medianBarTitle="Median stock path"
          subjectLabel={symbol}
        />

        <div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            Historical matches · tap to expand
          </p>
          <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
            {matches.map((match, index) => {
              const open = selectedMatch?.date === match.date;
              const t5 = match.forwardReturns?.t5;
              return (
                <li key={match.date}>
                  <button
                    type="button"
                    onClick={() => setSelectedMatchDate(open ? null : match.date)}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors sm:gap-3 sm:py-2.5 ${
                      open ? 'bg-gray-50' : 'hover:bg-gray-50/80'
                    }`}
                  >
                    <span className="w-4 shrink-0 text-[10px] tabular-nums text-gray-400">
                      {index + 1}
                    </span>
                    <span className="w-20 shrink-0 text-[11px] font-medium text-gray-800 sm:w-24 sm:text-xs">
                      {formatDateShort(match.date).replace(/, \d{4}$/, '')}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[11px] text-gray-500 sm:text-xs">
                      {match.headline}
                    </span>
                    <span className="hidden text-[10px] tabular-nums text-gray-400 sm:inline">
                      {(match.similarityScore * 100).toFixed(0)}%
                    </span>
                    <span
                      className={`w-12 shrink-0 text-right text-[11px] font-semibold tabular-nums sm:w-14 sm:text-xs ${
                        t5 != null ? pctChipClass(t5) : 'text-gray-400'
                      }`}
                    >
                      {t5 != null ? formatPct(t5) : '—'}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open ? (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden border-t border-gray-100 bg-gray-50/50"
                      >
                        <div className="space-y-2 px-3 py-3">
                          {match.matchReasons?.length ? (
                            <p className="text-[11px] leading-relaxed text-gray-500">
                              {match.matchReasons.slice(0, 3).join(' · ')}
                            </p>
                          ) : null}
                          {match.whatHappenedNext ? (
                            <p className="text-[11px] leading-relaxed text-gray-600">
                              {match.whatHappenedNext}
                            </p>
                          ) : null}
                          {match.pricePath?.length ? (
                            <AnalogForwardMiniChart
                              path={match.pricePath}
                              anchorDate={match.date}
                              anchorClose={match.anchorClose}
                            />
                          ) : null}
                        </div>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
